import { dayNumber, nthWeekdayOfMonth, type Time, ymd } from "./calendar";
import {
  abs,
  addUnit,
  clock,
  dayOfMonth,
  endOfMonth,
  endOfWeek,
  expandYear,
  firstOfNextMonth,
  monthDay,
  nextWorkdayAfter,
  rel,
  TIME_OF_DAY,
  thisWeekday,
  thisWeekend,
  toTime,
  type Unit,
  upcomingWeekday,
  weekdayInWeek,
} from "./common";
import { rx, Scanner } from "./scanner";
import type { DateRef, Edit, Env, Parsed, Rule } from "./types";

// ---------------------------------------------------------------------------
// Numbers

const CN = "([0-9]+|[〇零一二两三四五六七八九十百千]+)";
const CN_DIGITS: Record<string, number> = {
  〇: 0,
  零: 0,
  一: 1,
  二: 2,
  两: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
};
const CN_UNITS: Record<string, number> = { 十: 10, 百: 100, 千: 1000 };

/** Arabic or Chinese numerals: 3, 十五, 二十一, 两, 二〇二六, 一百. */
export function cnNum(s: string): number | null {
  if (/^[0-9]+$/.test(s)) return Number(s);
  if (!/[十百千]/.test(s)) {
    // Digit-by-digit (二〇二六).
    let n = 0;
    for (const ch of s) {
      const d = CN_DIGITS[ch];
      if (d === undefined) return null;
      n = n * 10 + d;
    }
    return n;
  }
  let total = 0;
  let cur = 0;
  for (const ch of s) {
    const d = CN_DIGITS[ch];
    if (d !== undefined) cur = d;
    else {
      const u = CN_UNITS[ch];
      if (u === undefined) return null;
      total += (cur || 1) * u;
      cur = 0;
    }
  }
  return total + cur;
}
const num = (s: string | undefined) => (s === undefined ? null : cnNum(s));

// ---------------------------------------------------------------------------
// Vocabulary

const WEEK = "(?:周|星期|礼拜|禮拜)";
const WDCH = "([一二三四五六日天七1-7])";
const WD_MAP: Record<string, number> = { 一: 1, 二: 2, 三: 3, 四: 4, 五: 5, 六: 6, 日: 7, 天: 7, 七: 7 };
const wdOf = (ch: string) => WD_MAP[ch] ?? Number(ch);

const UNIT = "(天|日|个?星期|个?礼拜|周|个月|年|个?小时|个?钟头|分钟)";
function unitOf(s: string): Unit {
  if (s === "天" || s === "日") return "day";
  if (s.includes("星期") || s.includes("礼拜") || s === "周") return "week";
  if (s === "个月") return "month";
  if (s === "年") return "year";
  if (s.includes("小时") || s.includes("钟头")) return "hour";
  return "minute";
}

const ymdText = (dn: number) => {
  const { y, m, d } = ymd(dn);
  return `${y}年${m}月${d}日`;
};

// ---------------------------------------------------------------------------
// Times

type Period = "early" | "morning" | "noon" | "afternoon" | "dusk" | "evening" | "night" | "midnight";
const PERIODS: [string, Period][] = [
  ["凌晨", "early"],
  ["半夜", "midnight"],
  ["午夜", "midnight"],
  ["早上", "morning"],
  ["早晨", "morning"],
  ["清晨", "morning"],
  ["上午", "morning"],
  ["早", "morning"],
  ["中午", "noon"],
  ["午后", "afternoon"],
  ["下午", "afternoon"],
  ["傍晚", "dusk"],
  ["晚上", "evening"],
  ["晚间", "evening"],
  ["夜间", "night"],
  ["夜里", "night"],
  ["深夜", "night"],
  ["晚", "evening"],
];
const PERIOD_RE = rx(`(${PERIODS.map(([w]) => w).join("|")})`);

const PERIOD_DEFAULT: Record<Period, Time> = {
  early: { h: 0, m: 0 },
  midnight: { h: 0, m: 0 },
  morning: TIME_OF_DAY.morning,
  noon: { h: 12, m: 0 },
  afternoon: TIME_OF_DAY.afternoon,
  dusk: { h: 18, m: 0 },
  evening: TIME_OF_DAY.evening,
  night: TIME_OF_DAY.night,
};

function applyPeriod(p: Period | undefined, t: Time): Time | null {
  if (!p) return t;
  const { h, m } = t;
  switch (p) {
    case "early":
    case "midnight":
      return h === 12 ? { h: 0, m } : h <= 6 ? t : null;
    case "morning":
      return h <= 12 ? t : null;
    case "noon":
      return h >= 11 && h <= 12 ? t : h >= 1 && h <= 2 ? { h: h + 12, m } : h >= 13 && h <= 14 ? t : null;
    default:
      if (h >= 1 && h < 12) return { h: h + 12, m };
      if (h === 12) return p === "afternoon" ? t : { h: 0, m };
      return h >= 12 ? t : null;
  }
}

/** 下午3点 / 晚上8点半 / 早上9:30 / 15:00 / 中午 / 3点15分 / 9点一刻. */
export function parseTimeZh(sc: Scanner): Time | null {
  return sc.try(() => {
    sc.eat(rx("[@在于]"));
    const pm = sc.eat(PERIOD_RE);
    const period = pm ? PERIODS.find(([w]) => w === pm[1])?.[1] : undefined;
    let m = sc.eat(rx("(\\d{1,2})[:：](\\d{2})"));
    let t: Time | null = null;
    if (m) t = clock(Number(m[1]), Number(m[2]));
    else {
      m = sc.eat(rx(`${CN}\\s*[点點时時](?:(半)|(一刻)|(三刻)|(?:${CN}\\s*分?))?钟?`));
      if (m) {
        const h = num(m[1]);
        const mi = m[2] ? 30 : m[3] ? 15 : m[4] ? 45 : m[5] ? num(m[5]) : 0;
        if (h === null || mi === null) return null;
        t = clock(h, mi);
      }
    }
    if (t) return applyPeriod(period, t);
    return period ? PERIOD_DEFAULT[period] : null;
  });
}

// ---------------------------------------------------------------------------
// Dates

interface DateHit {
  ref: DateRef;
  minutes?: number;
  /** "今晚" also fixes a time. */
  time?: Time;
}

function calendarDate(sc: Scanner, env: Env): DateRef | null {
  let m = sc.eat(rx("(\\d{4})[-/.](\\d{1,2})[-/.](\\d{1,2})"));
  if (m) return monthDay(env, Number(m[2]), Number(m[3]), Number(m[1]));
  m = sc.eat(rx("(\\d{1,2})/(\\d{1,2})/(\\d{4})"));
  if (m) {
    let mo = Number(m[1]);
    let d = Number(m[2]);
    if (mo > 12) [mo, d] = [d, mo];
    return monthDay(env, mo, d, Number(m[3]));
  }
  m = sc.eat(rx("(\\d{1,2})/(\\d{1,2})"));
  if (m) {
    let mo = Number(m[1]);
    let d = Number(m[2]);
    if (mo > 12) [mo, d] = [d, mo];
    return monthDay(env, mo, d);
  }
  m = sc.eat(rx(`(?:${CN}年)?${CN}月(?:份)?(?:的)?(?:${CN}\\s*[日号號]|(中旬)|(底|末)|(初))`));
  if (m) {
    const year = m[1] !== undefined ? num(m[1]) : undefined;
    const mo = num(m[2]);
    if (mo === null || year === null) return null;
    const y = year === undefined ? undefined : expandYear(year);
    if (m[4]) return monthDay(env, mo, 15, y);
    if (m[6]) return monthDay(env, mo, 1, y);
    if (m[5]) {
      const first = monthDay(env, mo, 1, y);
      return first ? abs(endOfMonth(first.dn)) : null;
    }
    const d = num(m[3]);
    return d === null ? null : monthDay(env, mo, d, y);
  }
  // 1月的第3个周五 / 1月最后一个周五
  m = sc.eat(rx(`${CN}月(?:份)?的?(?:第${CN}个?|(最后一个?))${WEEK}${WDCH}`));
  if (m) {
    const mo = num(m[1]);
    const n = m[3] ? -1 : num(m[2]);
    if (mo === null || n === null || mo < 1 || mo > 12 || n === 0 || n > 5) return null;
    const wd = wdOf(m[4] as string);
    const { y } = ymd(env.today);
    for (let i = 0; i < 3; i++) {
      const d = nthWeekdayOfMonth(y + i, mo, n, wd);
      if (d !== null && d >= env.today) return abs(d);
    }
    return null;
  }
  return null;
}

function holiday(sc: Scanner, env: Env): DateRef | null {
  const table: [string, number, number][] = [
    ["新年前夜|跨年夜", 12, 31],
    ["新年|元旦", 1, 1],
    ["情人节", 2, 14],
    ["万圣节", 10, 31],
    ["平安夜", 12, 24],
    ["圣诞节?", 12, 25],
  ];
  for (const [src, m, d] of table) if (sc.eat(rx(`(?:${src})`))) return monthDay(env, m, d);
  return null;
}

function baseDateZh(sc: Scanner, env: Env): DateHit | null {
  const { today } = env;
  const r = (dn: number | null | undefined): DateHit | null => (dn == null ? null : { ref: rel(dn) });
  const hit = (ref: DateRef | null): DateHit | null => (ref ? { ref } : null);
  return sc.try((): DateHit | null => {
    let m: RegExpExecArray | null;
    if (sc.eat(rx("今晚"))) return { ref: rel(today), time: TIME_OF_DAY.evening };
    if (sc.eat(rx("明晚"))) return { ref: rel(today + 1), time: TIME_OF_DAY.evening };
    if (sc.eat(rx("(?:今天|今日)"))) return r(today);
    if (sc.eat(rx("(?:明天|明日)"))) return r(today + 1);
    if (sc.eat(rx("大后天"))) return r(today + 3);
    if (sc.eat(rx("后天"))) return r(today + 2);
    if (sc.eat(rx("(?:昨天|昨日)"))) return r(today - 1);
    if (sc.eat(rx("大前天"))) return r(today - 3);
    if (sc.eat(rx("前天"))) return r(today - 2);
    if (sc.eat(rx("(?:某天|某一天|改天)"))) return r(addUnit(today, 0, "month", 2).dn);
    if (sc.eat(rx("下一?个工作日"))) return r(nextWorkdayAfter(today));
    if (sc.eat(rx("(?:下个?周末|下个?星期末)"))) return r(thisWeekend(env) + 7);
    if (sc.eat(rx("(?:这个?周末|本周末|周末)"))) return r(thisWeekend(env));
    if (sc.eat(rx("(?:稍后这周|这周稍后|本周晚些时候|这周晚些时候)")))
      return r(Math.max(today, Math.min(today + 2, endOfWeek(env))));
    m = sc.eat(rx(`(下下|下个?|这个?|本|上个?)?${WEEK}${WDCH}`));
    if (m) {
      const wd = wdOf(m[2] as string);
      const p = m[1];
      if (p === "下下") return r(weekdayInWeek(env, wd, 2));
      if (p?.startsWith("下")) return r(weekdayInWeek(env, wd, 1));
      if (p?.startsWith("上")) return r(weekdayInWeek(env, wd, -1));
      if (p) return r(thisWeekday(env, wd));
      return r(upcomingWeekday(env, wd));
    }
    if (sc.eat(rx(`(?:下下个?${WEEK})`))) return r(weekdayInWeek(env, env.nextWeek, 2));
    if (sc.eat(rx(`(?:下个?${WEEK})`))) return r(weekdayInWeek(env, env.nextWeek, 1));
    if (sc.eat(rx("(?:这个?|本)(?:周|星期)"))) return r(Math.max(today, endOfWeek(env)));
    if (sc.eat(rx("(?:下个月|下月)"))) return r(firstOfNextMonth(today));
    if (sc.eat(rx("(?:本月底|这个月底|月底|月末|本月|这个月)"))) return r(endOfMonth(today));
    if (sc.eat(rx("(?:明年)"))) return r(dayNumber(ymd(today).y + 1, 1, 1));
    if (sc.eat(rx("(?:年底|年末|今年)"))) return r(dayNumber(ymd(today).y, 12, 31));

    // N天后 / N天内 / +N天 / 一周后 / 3个月后 / 2小时后
    m = sc.eat(rx(`(?:\\+\\s*)${CN}\\s*${UNIT}`)) ?? sc.eat(rx(`${CN}\\s*${UNIT}\\s*(?:以后|之后|后|内|以内|之内)`));
    if (m) {
      const n = num(m[1]);
      if (n === null) return null;
      const u = unitOf(m[2] as string);
      const res = addUnit(today, env.nowMinutes, u, n);
      return u === "minute" || u === "hour" ? { ref: rel(res.dn), minutes: res.minutes } : r(res.dn);
    }
    if (sc.eat(rx("半(?:个)?小时(?:以后|之后|后|内)"))) {
      const res = addUnit(today, env.nowMinutes, "minute", 30);
      return { ref: rel(res.dn), minutes: res.minutes };
    }
    const cal = calendarDate(sc, env);
    if (cal) return hit(cal);
    const h = holiday(sc, env);
    if (h) return hit(h);
    m = sc.eat(rx(`${CN}\\s*[日号號]`));
    if (m) {
      const d = num(m[1]);
      return d === null ? null : hit(dayOfMonth(env, d));
    }
    return null;
  });
}

/** A date, optionally followed by an offset: 新年之前50天, 7月21日之后28天. */
function parseDateZh(sc: Scanner, env: Env): DateHit | null {
  return sc.try(() => {
    const base = baseDateZh(sc, env);
    if (!base) return null;
    if (base.minutes !== undefined || base.time) return base;
    const off = sc.try(() => {
      const m = sc.eat(rx(`(之前|以前|前|之后|以后|后)${CN}\\s*${UNIT}`));
      if (!m) return null;
      const n = num(m[2]);
      const u = unitOf(m[3] as string);
      if (n === null || u === "minute" || u === "hour") return null;
      return addUnit(base.ref.dn, 0, u, m[1]?.includes("前") ? -n : n).dn;
    });
    return off === null ? base : { ref: rel(off) };
  });
}

// ---------------------------------------------------------------------------
// Non-recurring

const SEP = rx("[,，的@在于]");

function parseOnce(sc: Scanner, env: Env): Parsed | null {
  let date: DateHit | undefined;
  let time: Time | undefined;
  while (!sc.done()) {
    if (sc.eat(SEP)) continue;
    if (!date) {
      const d = parseDateZh(sc, env);
      if (d) {
        date = d;
        if (d.time) {
          if (time) return null;
          time = d.time;
        }
        continue;
      }
    }
    if (!time) {
      const t = parseTimeZh(sc);
      if (t) {
        time = t;
        continue;
      }
    }
    return null;
  }
  if (date?.minutes !== undefined) return time ? null : { kind: "once", dn: date.ref.dn, time: toTime(date.minutes) };
  if (date) return { kind: "once", dn: date.ref.dn, time };
  if (time) {
    const passed = time.h * 60 + time.m <= env.nowMinutes;
    return { kind: "once", dn: env.today + (passed ? 1 : 0), time };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Recurring

const LIST_SEP = "[、,，和及与跟]";

/** 一三五 / 一、三、五 / 一、周五 / 一和周五 (the first 周 already consumed when `bare`). */
function weekdayList(sc: Scanner, bare: boolean): number[] | null {
  const out: number[] = [];
  const first = sc.eat(rx(bare ? WDCH : `${WEEK}${WDCH}`));
  if (!first) return null;
  out.push(wdOf(first[1] as string));
  for (;;) {
    const m = sc.eat(rx(`${LIST_SEP}?\\s*(?:每个?)?${WEEK}?${WDCH}(?![点點时時:：号號日月])`));
    if (!m) break;
    out.push(wdOf(m[1] as string));
  }
  return [...new Set(out)];
}

/** 15号 / 最后一天 / 第二个周一 / 最后一个周五 / 第一个工作日 / 2号、15号、27号. */
function monthItems(sc: Scanner, rule: Rule): boolean {
  const days: number[] = [];
  const nth: { n: number; wd: number }[] = [];
  const work: number[] = [];
  let count = 0;
  for (;;) {
    if (count > 0 && !sc.eat(rx(LIST_SEP))) break;
    const ok = sc.try(() => {
      if (sc.eat(rx("最后一天"))) return days.push(-1);
      if (sc.eat(rx("第一天"))) return days.push(1);
      let m = sc.eat(rx(`(?:第${CN}|(最后(?:一|1)?))个?工作日`));
      if (m) {
        const n = m[2] ? -1 : num(m[1]);
        return n ? work.push(n) : null;
      }
      m = sc.eat(rx(`(?:第${CN}|(最后(?:一|1)?))个?${WEEK}${WDCH}`));
      if (m) {
        const n = m[2] ? -1 : num(m[1]);
        if (!n || n > 5) return null;
        return nth.push({ n, wd: wdOf(m[3] as string) });
      }
      m = sc.eat(rx(`${CN}\\s*[日号號](?![\\d])`));
      if (m) {
        const d = num(m[1]);
        return d && d <= 31 ? days.push(d) : null;
      }
      return null;
    });
    if (!ok) {
      if (count > 0) return false;
      return false;
    }
    count++;
  }
  rule.freq = "month";
  if (days.length) rule.monthDays = days;
  if (nth.length) rule.nthWeekdays = nth;
  if (work.length) rule.nthWorkdays = work;
  return true;
}

/** 3月5日 / 1月第1个周三 / 1月14日、4月14日和9月15日. */
function yearItems(sc: Scanner, rule: Rule): boolean {
  const dates: { m: number; d: number }[] = [];
  const nth: { m: number; n: number; wd: number }[] = [];
  let count = 0;
  for (;;) {
    if (count > 0) {
      const save = sc.pos;
      if (!sc.eat(rx(LIST_SEP))) break;
      sc.eat(rx("每年|每个"));
      if (!sc.try(() => (sc.eat(rx(`${CN}月`)) ? true : null))) {
        sc.pos = save;
        break;
      }
      sc.pos = save;
      sc.eat(rx(LIST_SEP));
      sc.eat(rx("每年|每个"));
    }
    const ok = sc.try(() => {
      let m = sc.eat(rx(`${CN}月的?(?:第${CN}|(最后(?:一|1)?))个?${WEEK}${WDCH}`));
      if (m) {
        const mo = num(m[1]);
        const n = m[3] ? -1 : num(m[2]);
        if (!mo || mo > 12 || !n || n > 5) return null;
        return nth.push({ m: mo, n, wd: wdOf(m[4] as string) });
      }
      m = sc.eat(rx(`${CN}月${CN}\\s*[日号號]`));
      if (m) {
        const mo = num(m[1]);
        const d = num(m[2]);
        if (!mo || !d || !monthDay({ today: 0, nowMinutes: 0, startDay: 1, nextWeek: 1, weekendStartDay: 6 }, mo, d))
          return null;
        return dates.push({ m: mo, d });
      }
      return null;
    });
    if (!ok) break;
    count++;
  }
  if (!count) return false;
  rule.freq = "year";
  if (dates.length) rule.yearDates = dates;
  if (nth.length) rule.yearNth = nth;
  return true;
}

/** The part after 每: 天, 两周, 个工作日, 周一三五, 月15号, 年3月5日, 个周一、周五, 3小时, 季度 … */
function recurrenceBody(sc: Scanner, rule: Rule, env: Env, skipped: boolean): Time | undefined | null {
  // 每个27日 / 每个1月14日 / 每个周一 — 个 here is a measure word.
  sc.eat(rx("个(?=[0-9〇零一二两三四五六七八九十周星礼工季小月])"));
  let interval: number | undefined;
  const n = sc.try(() => {
    const m = sc.eat(rx(CN));
    if (!m) return null;
    const save = sc.pos;
    const u = sc.eat(rx(`(?:${UNIT}|个?工作日|个?季度)`));
    sc.pos = save;
    return u && !/^[日]/.test(u[0]) ? num(m[1]) : u?.[0] === "日" ? null : null;
  });
  if (n !== null) {
    if (n < 1) return null;
    interval = skipped ? n + 1 : n;
  } else if (skipped) interval = 2;

  const setInterval = (k = 1) => {
    rule.interval = (interval ?? 1) * k;
  };

  if (sc.eat(rx("个?工作日"))) {
    rule.freq = "day";
    rule.workdays = true;
    setInterval();
    return undefined;
  }
  if (sc.eat(rx("个?季度"))) {
    rule.freq = "month";
    setInterval(3);
    return undefined;
  }
  if (sc.eat(rx("个?(?:小时|钟头)"))) {
    rule.freq = "hour";
    setInterval();
    return undefined;
  }
  if (sc.eat(rx("(?:天|日)"))) {
    rule.freq = "day";
    setInterval();
    return undefined;
  }
  if (sc.eat(rx("(?:早上|早晨|上午|早)(?![0-9〇零一二两三四五六七八九十]+[点點时時:])"))) {
    rule.freq = "day";
    return TIME_OF_DAY.morning;
  }
  if (sc.eat(rx("(?:晚上|晚)(?![0-9〇零一二两三四五六七八九十]+[点點时時:])"))) {
    rule.freq = "day";
    return TIME_OF_DAY.evening;
  }
  if (sc.eat(rx("个?月"))) {
    setInterval();
    rule.freq = "month";
    sc.eat(rx("的"));
    const save = sc.pos;
    if (!monthItems(sc, rule)) sc.pos = save;
    if (rule.freq !== "month") return null;
    return undefined;
  }
  if (sc.eat(rx("一?年"))) {
    rule.freq = "year";
    setInterval();
    sc.eat(rx("的"));
    yearItems(sc, rule);
    return undefined;
  }
  // 周 / 星期 (+ optional weekday list, 末 for weekend)
  if (sc.eat(rx("(?:一|个)?(?:周|个?星期|个?礼拜)"))) {
    rule.freq = "week";
    setInterval();
    if (sc.eat(rx("末"))) {
      rule.weekdays = [env.weekendStartDay, (env.weekendStartDay % 7) + 1];
      return undefined;
    }
    sc.eat(rx("的"));
    const wds = weekdayList(sc, true) ?? weekdayList(sc, false);
    if (wds) rule.weekdays = wds;
    return undefined;
  }
  // 每个周一、周五 / 每周末 handled above; bare 周X list after 个.
  const wds = weekdayList(sc, false);
  if (wds) {
    rule.freq = "week";
    rule.weekdays = wds;
    setInterval();
    return undefined;
  }
  if (sc.eat(rx("(?:个)?周末"))) {
    rule.freq = "week";
    rule.weekdays = [env.weekendStartDay, (env.weekendStartDay % 7) + 1];
    return undefined;
  }
  // 每个1月14日 … / 每个27日
  const save = sc.pos;
  if (yearItems(sc, rule)) return undefined;
  sc.pos = save;
  if (monthItems(sc, rule)) return undefined;
  return null;
}

function parseRecurringZh(sc: Scanner, env: Env): Parsed | null {
  const rule: Rule = { freq: "day", interval: 1, fromCompletion: false };
  let time: Time | undefined;
  let start: DateRef | undefined;
  let startTime: Time | undefined;
  let end: DateRef | undefined;
  const edits: Edit[] = [];
  let haveHead = false;

  while (!sc.done()) {
    if (sc.eat(SEP)) continue;
    if (!haveHead) {
      const head = sc.eat(rx("每\\s*(!)?\\s*(隔)?"));
      if (head) {
        haveHead = true;
        if (head[1]) rule.fromCompletion = true;
        const t = recurrenceBody(sc, rule, env, !!head[2]);
        if (t === null) return null;
        if (t) time = t;
        continue;
      }
    }
    // 从X开始 / 从X起 / X起 / X开始
    if (!start && !startTime) {
      const s = sc.try(() => {
        const from = sc.eat(rx("(?:从|自)")) !== null;
        const d = parseDateZh(sc, env);
        const t = parseTimeZh(sc);
        if (!d && !t) return null;
        if (!sc.eat(rx("(?:开始|起)")) && !from) return null;
        if (d?.minutes !== undefined) return null;
        return { d, t };
      });
      if (s) {
        if (s.d) start = s.d.ref;
        if (s.t ?? s.d?.time) startTime = s.t ?? s.d?.time;
        continue;
      }
    }
    if (!end) {
      // 直到X / 到X(为止) / X为止 / X截止
      const e = sc.try(() => {
        const kw = sc.eat(rx("(?:直到|一直到|到|至|截至|截止到?)")) !== null;
        sc.ws();
        const from = sc.pos;
        const d = parseDateZh(sc, env);
        if (!d || d.minutes !== undefined) return null;
        const to = sc.pos;
        const tail = sc.eat(rx("(?:为止|截止|结束)")) !== null;
        if (!kw && !tail) return null;
        return { ref: d.ref, from, to };
      });
      if (e) {
        end = e.ref;
        if (!end.absolute) edits.push({ from: e.from, to: e.to, text: ymdText(end.dn) });
        continue;
      }
      // 3周内
      const from = sc.pos;
      const m = sc.eat(rx(`${CN}\\s*${UNIT}\\s*(?:内|以内|之内)`));
      if (m) {
        const n = num(m[1]);
        const u = unitOf(m[2] as string);
        if (n === null || u === "minute" || u === "hour") return null;
        end = rel(addUnit(start?.dn ?? env.today, 0, u, n).dn - 1);
        edits.push({ from, to: sc.pos, text: `直到${ymdText(end.dn)}` });
        continue;
      }
    }
    if (!time) {
      const t = parseTimeZh(sc);
      if (t) {
        time = t;
        continue;
      }
    }
    return null;
  }
  if (!haveHead) return null;
  return { kind: "recur", rule, time, start, startTime, end, edits };
}

/** Parses a whitespace-normalized Chinese due string. */
export function parseZh(s: string, env: Env): Parsed | null {
  if (/^(?:无日期|没有日期|无截止日期|不设日期|无)$/.test(s.replace(/\s+/g, ""))) return { kind: "clear" };
  if (s.includes("每")) {
    const r = parseRecurringZh(new Scanner(s, /[a-z0-9]/), env);
    if (r) return r;
  }
  return parseOnce(new Scanner(s, /[a-z0-9]/), env);
}
